import { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { usePayPalScriptReducer } from '@paypal/react-paypal-js';
import {
	callGetOrderApi,
	GetOrderResData,
	callGetSiteSettlementApi,
	GetSiteSettlementResData,
} from 'requests/RequestPayment';
import { HttpStatus } from 'consts/HttpStatus';
import { RoutePath } from 'consts/RoutePath';
import { callCheckWhiteListApi } from 'requests/RequestWhiteList';
import { PaymentType, isPayPal, OrderType, OrderTypeEntity, Page } from 'consts/PaymentConst';

export const useReceivePaymentRequest = (
	orderType: OrderType,
	setPage: React.Dispatch<React.SetStateAction<number>>,
) => {
	const [userAgent] = useState(window.navigator.userAgent.toLowerCase());
	const history = useHistory();
	const location = useLocation();
	const search = location.search;
	const queryParams = new URLSearchParams(search);
	const gid_order_token = queryParams.get('gid_order'); // 注文情報認可コード
	const redirect_uri = queryParams.get('redirect_uri');
	const client_id = queryParams.get('client_id');
	const state = queryParams.get('state');
	const [error, setError] = useState('');
	const [siteSettlement, setSiteSettlement] = useState<GetSiteSettlementResData>(undefined);
	const [order, setOrder] = useState<GetOrderResData>(undefined);
	const [getOrderLoading, setGetOrderLoading] = useState(true);
	const [{ options }, dispatch] = usePayPalScriptReducer();

	// エラー時に遷移するページが指定されていればボタンと遷移先を変更してエラーページに飛ばす
	const transErrorPage = () => {
		// 通常エラーページ
		if (!siteSettlement?.error_button_display || !siteSettlement?.error_return_url) {
			history.push(RoutePath.ERROR);
			return;
		}
		// サービス用エラーページ
		setPage(Page.SERVICE_ERROR);
	};

	// IEだった場合に非対応ページに飛ばす
	const transUnSupportedPage = () => {
		setPage(Page.UN_SUPPORTED);
	};

	// サービスサイト決済設定情報取得
	const [getSiteSettlementLoading, setGetSiteSettlementLoading] = useState(true);
	useEffect(() => {
		if (!gid_order_token || !redirect_uri || !client_id) {
			history.push(RoutePath.ERROR);
			return;
		}
		const execute = async () => {
			const getSiteSettlementResult = await callGetSiteSettlementApi(client_id);
			if (getSiteSettlementResult.status !== HttpStatus.OK) {
				history.push(RoutePath.ERROR);
				return;
			}
			setSiteSettlement(getSiteSettlementResult.data);

			// IEなら非対応画面へ（戻り先情報が欲しいためこのタイミングで判定している）
			if (userAgent.indexOf('msie') !== -1 || userAgent.indexOf('trident') !== -1) {
				transUnSupportedPage();
				setGetOrderLoading(false);
				return;
			}

			setGetSiteSettlementLoading(false);
		};
		execute();
	}, [gid_order_token]);

	// 注文情報認証
	useEffect(() => {
		if (getSiteSettlementLoading) {
			return;
		}

		const execute = async () => {
			const whiteListResult = await callCheckWhiteListApi({
				burl: redirect_uri,
				scd: client_id,
			});
			if (whiteListResult.status === HttpStatus.REDIRECT) {
				transErrorPage();
				setGetOrderLoading(false);
				return;
			}

			const getOrderResult = await callGetOrderApi(gid_order_token);
			// APIエラー or 注文区分とページが合っていない場合はエラー
			if (getOrderResult.status !== HttpStatus.OK || orderType.key !== getOrderResult.data.order_type) {
				transErrorPage();
				setGetOrderLoading(false);
				return;
			}
			setOrder(getOrderResult.data);
			// クライアントトークン更新
			dispatch({
				type: 'resetOptions',
				value: {
					...options,
					'data-client-token': getOrderResult.data.paypal_client_token,
					'merchant-id': getOrderResult.data.merchant_id,
				},
			});

			setGetOrderLoading(false);
		};
		execute();
	}, [getSiteSettlementLoading]);

	// options変更処理（PayPal払いの場合にdata-client-token不要、定期購読のPayPal決済時merchant-idは不要）
	const resetOptions = (orderType: OrderType, payment: PaymentType) => {
		// 都度決済
		if (orderType.key === OrderTypeEntity.PAYMENT.key) {
			dispatch({
				type: 'resetOptions',
				value: {
					...options,
					'data-client-token': isPayPal(payment) ? undefined : order.paypal_client_token,
					'merchant-id': order.merchant_id,
				},
			});
		}
		// 定期購読
		else {
			dispatch({
				type: 'resetOptions',
				value: {
					...options,
					'data-client-token': isPayPal(payment) ? undefined : order.paypal_client_token,
					'merchant-id': isPayPal(payment) ? undefined : order.merchant_id,
				},
			});
		}
	};

	return {
		getOrderLoading,
		order,
		redirect_uri,
		state,
		error,
		setError,
		resetOptions,
		transErrorPage,
		siteSettlement,
	};
};
